// MACHINERY V2 (Stroje) — ChromeNavV4 module table + visibility gate (M2.1).
//
// The nav's configuration, split out of ChromeNavV4.tsx so the bar stays
// presentational and this table is the ONE place the M2.12 promotion edits
// (paths, ordering, a new module, the accent source). `canSeeModule` lives
// here too so the tabs, the bell, the Midas button and the user-menu items
// all gate through ONE implementation instead of three copies.
//
// Accents are the approved module colours from
// design_handoff_machinery_v3/palette-tokens.css, carried here exactly as the
// handoff's NAV_ACCENTS map carries them. Stroje reads the scoped CSS var
// because a v2 page always has .mod-stroje on its root; the three siblings
// carry their approved hex. That is why the .mod-servicehub / .mod-network /
// .mod-operacie CSS blocks stay dropped from machinery-v2-tokens.scss — see
// the marker comment there, and re-open it at M2.12.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import permissionContext from "../../../../core/context/permissionContext";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";
import { NgMachine, NgNetwork, NgOps, NgService } from "./ChromeNavIcons";

export type ChromeNavModuleKey = "stroje" | "servicehub" | "network" | "operacie";

export interface NavModule {
  key: ChromeNavModuleKey;
  labelKey: string;
  path: string;
  module: string;
  /** Active-tab + avatar tint. Inactive tabs are never tinted. */
  accent: string;
  Icon: React.FC;
  /** Mirrors the legacy bar's visibleFor:"operator" sections. */
  operatorOnly: boolean;
}

/** M2.12: flip to "/machinery" when v2 becomes the default surface. */
export const STROJE_PATH = "/machinery-v2";

export const NAV_MODULES: NavModule[] = [
  {
    key: "stroje",
    labelKey: "layout:chromeV4.stroje",
    path: STROJE_PATH,
    module: Modules.Machinery,
    accent: "var(--mod-accent)",
    Icon: NgMachine,
    operatorOnly: true,
  },
  {
    key: "servicehub",
    labelKey: "layout:chromeV4.servicehub",
    path: "/service-board",
    module: Modules.Servicing,
    accent: "#33739E",
    Icon: NgService,
    operatorOnly: false,
  },
  {
    key: "network",
    labelKey: "layout:chromeV4.network",
    path: "/companies",
    module: Modules.PartnerHub,
    accent: "#8A5A83",
    Icon: NgNetwork,
    operatorOnly: false,
  },
  {
    key: "operacie",
    labelKey: "layout:chromeV4.operacie",
    path: "/operations",
    module: Modules.Maintenance,
    accent: "#A8763E",
    Icon: NgOps,
    operatorOnly: true,
  },
];

/** Read gate with the legacy TRANSITION-MODE escape hatch: a token carrying NO
 *  permissions claim sees everything, exactly as Header.tsx:424 does. Reads the
 *  current JWT on every call, so it is correct across a token refresh. */
export const canSeeModule = (module: string): boolean =>
  !permissionContext.hasPermissionsClaim() ||
  permissionContext.hasPermission(module, PermissionLevel.Read);
